import { useId, useState, type PointerEvent, type ReactNode } from 'react';
import {
  columnChoices,
  isCustomLayout,
  layoutOf,
  moveColumn,
  toggleColumn,
  type ColumnChoice,
  type TableLayout,
} from '@/domain';
import { strings } from '@/shared/strings';
import { ChevronIcon, ChevronUpIcon, GripIcon, LineIcon, LockIcon } from '@/ui/icons';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import styles from './CustomizeColumns.module.css';

/**
 * The words of the control. A table's own unless its owner says otherwise: a
 * screen of widgets is arranged by this same sheet, and says "this screen"
 * where a table says "this table". The lab's request of 12 October 2026.
 */
export interface CustomizeText {
  open: string;
  title: string;
  lead: string;
  listLabel: string;
  alwaysShown: string;
  drag: (name: string) => string;
  moveUp: (name: string) => string;
  moveDown: (name: string) => string;
  rowsPerPage: string;
  reset: string;
  done: string;
  close: string;
  saved: string;
}

/** One of the table's columns, as the list names it. */
export interface CustomizeColumn {
  key: string;
  label: string;
  /** One line under the name on what it shows, for a list whose names need it: a screen's widgets. */
  about?: string;
  /** An essential column: always shown, and says so. */
  locked?: boolean;
  /** One the table offers and leaves out until it is ticked. The request of 8 October 2026 (second). */
  optional?: boolean;
}

/**
 * What a table needs to be laid out by its reader: the layout kept for them,
 * and how to keep another. `useTableLayout` returns the first three; a paged
 * table adds its rows a page.
 */
export interface TableCustomize {
  layout: TableLayout | null;
  save: (layout: TableLayout) => void;
  reset: () => void;
  /** The rows a page, for a table that has pages. */
  rows?: { size: number; sizes: readonly number[]; choose: (size: number) => void };
  /**
   * Lay the table out and offer nothing over it: for the second and later of
   * several tables that are one table to their reader, the price list's
   * categories among them, where the first carries the control for all.
   */
  quiet?: boolean;
}

/**
 * Customize columns: the one control every table has for it. The request of
 * 7 October 2026, section 3.
 *
 * A button over the table opens a sheet listing the table's columns in the
 * reader's order. A tick shows or hides one; the handle drags it to another
 * place, and the two arrows move it a step for a keyboard or a hand that would
 * rather not drag. An essential column, the job order number among them, can
 * be moved and cannot be hidden. A paged table offers its rows a page here too.
 *
 * Under the columns the table comes with are the ones it only offers, without
 * a tick: what its rows already carry and do not show. Ticking one draws it.
 * The request of 8 October 2026 (second).
 *
 * Every change is kept at once, on the reader's account, and the table behind
 * the sheet redraws as they go: there is nothing to save and so nothing to
 * lose by closing. Reset to default puts the table back as it comes.
 */
export function CustomizeColumns({
  columns,
  customize,
  text = strings.table.customize,
  icon = <LineIcon />,
}: {
  columns: readonly CustomizeColumn[];
  customize: TableCustomize;
  /** The control's words, where what it arranges is not a table's columns. */
  text?: CustomizeText;
  /** The mark on the button that opens it. */
  icon?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className={styles.open} onClick={() => setOpen(true)}>
        {icon}
        {text.open}
      </button>
      {open && (
        <ColumnsSheet columns={columns} customize={customize} text={text} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function ColumnsSheet({
  columns,
  customize,
  text,
  onClose,
}: {
  columns: readonly CustomizeColumn[];
  customize: TableCustomize;
  text: CustomizeText;
  onClose: () => void;
}) {
  const rowsId = useId();
  const kept = columnChoices(columns, customize.layout);
  // The list while a column is being dragged; what is kept the rest of the time.
  const [dragged, setDragged] = useState<{ key: string; choices: ColumnChoice[] } | null>(null);
  const choices = dragged?.choices ?? kept;
  const labelOf = (key: string) => columns.find((column) => column.key === key)?.label ?? key;
  const aboutOf = (key: string) => columns.find((column) => column.key === key)?.about;
  const pageSize = customize.layout?.pageSize ?? null;

  const keep = (next: ColumnChoice[]) => customize.save(layoutOf(next, pageSize));

  function startDrag(event: PointerEvent<HTMLButtonElement>, key: string) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragged({ key, choices: kept });
  }

  function drag(event: PointerEvent<HTMLButtonElement>) {
    if (dragged === null) return;
    // The row under the finger, whichever element of it that is.
    const over = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>('[data-column]')?.dataset['column'];
    if (over === undefined || over === dragged.key) return;
    const from = dragged.choices.findIndex((choice) => choice.key === dragged.key);
    const to = dragged.choices.findIndex((choice) => choice.key === over);
    if (to < 0) return;
    setDragged({ key: dragged.key, choices: moveColumn(dragged.choices, from, to) });
  }

  function endDrag() {
    if (dragged === null) return;
    const moved = dragged.choices.some((choice, index) => choice.key !== kept[index]?.key);
    if (moved) keep(dragged.choices);
    setDragged(null);
  }

  return (
    <BottomSheet
      title={text.title}
      lead={text.lead}
      closeLabel={text.close}
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <Button
            onClick={customize.reset}
            disabled={!isCustomLayout(columns, customize.layout)}
          >
            {text.reset}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {text.done}
          </Button>
        </div>
      }
    >
      <ol className={styles.list} aria-label={text.listLabel}>
        {choices.map((choice, index) => {
          const label = labelOf(choice.key);
          const about = aboutOf(choice.key);
          return (
            <li
              key={choice.key}
              data-column={choice.key}
              className={`${styles.row} ${dragged?.key === choice.key ? styles.dragging : ''}`}
            >
              <button
                type="button"
                className={styles.grip}
                aria-label={text.drag(label)}
                title={text.drag(label)}
                onPointerDown={(event) => startDrag(event, choice.key)}
                onPointerMove={drag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <GripIcon />
              </button>

              <label className={styles.name}>
                <input
                  type="checkbox"
                  checked={choice.shown}
                  disabled={choice.locked}
                  onChange={() => keep(toggleColumn(choices, choice.key))}
                />
                {about === undefined ? (
                  <span>{label}</span>
                ) : (
                  <span className={styles.both}>
                    <span>{label}</span>
                    <span className={styles.about}>{about}</span>
                  </span>
                )}
                {choice.locked && (
                  <span className={styles.locked}>
                    <LockIcon size={12} />
                    {text.alwaysShown}
                  </span>
                )}
              </label>

              <button
                type="button"
                className={styles.step}
                aria-label={text.moveUp(label)}
                title={text.moveUp(label)}
                disabled={index === 0}
                onClick={() => keep(moveColumn(choices, index, index - 1))}
              >
                <ChevronUpIcon />
              </button>
              <button
                type="button"
                className={styles.step}
                aria-label={text.moveDown(label)}
                title={text.moveDown(label)}
                disabled={index === choices.length - 1}
                onClick={() => keep(moveColumn(choices, index, index + 1))}
              >
                <ChevronIcon />
              </button>
            </li>
          );
        })}
      </ol>

      {customize.rows !== undefined && (
        <div className={styles.rows}>
          <label htmlFor={rowsId}>{text.rowsPerPage}</label>
          <select
            id={rowsId}
            value={customize.rows.size}
            onChange={(event) => customize.rows?.choose(Number(event.target.value))}
          >
            {customize.rows.sizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      <p className={styles.saved}>{text.saved}</p>
    </BottomSheet>
  );
}
